import { IRequestOptions, TResponseResult } from 'models/api.model'

import { ApiService } from 'services/api.service'

import { AppName, RequestMethod } from 'root-constants'

export class SubscriptionsApi {
  private api: ApiService

  constructor(api: ApiService) {
    this.api = api
  }

  pauseSubscription({
    subscriptionId,
    token,
    appName,
  }: {
    subscriptionId: string
    token: string
    appName: AppName
  }): Promise<TResponseResult<{ status: boolean }>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      params: {
        token,
        app_name: appName,
      },
    }

    return this.api.makeRequest<{ status: boolean }>(
      `v1/subscriptions/${subscriptionId}/pause`,
      options,
    )
  }

  pauseSubscriptionByEmail({
    subscriptionId,
    email,
    appName,
  }: {
    subscriptionId: string
    email: string
    appName: AppName
  }): Promise<TResponseResult<{ status: boolean }>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      params: {
        email,
        app_name: appName,
      },
    }

    return this.api.makeRequest<{ status: boolean }>(
      `v1/subscriptions/${subscriptionId}/pause`,
      options,
    )
  }

  unsubscribeByToken({
    subscriptionId,
    token,
    appName,
    reason = '',
  }: {
    subscriptionId: string
    token: string
    appName: string
    reason: string
  }): Promise<TResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.DELETE,
      params: {
        token,
        app_name: appName,
      },
      body: {
        reason,
      },
    }

    return this.api.makeRequest<never>(
      `v1/subscriptions/${subscriptionId}`,
      options,
    )
  }

  unsubscribeByEmail({
    subscriptionId,
    email,
    appName,
    reason = '',
  }: {
    subscriptionId: string
    email: string
    appName: string
    reason: string
  }): Promise<TResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.DELETE,
      params: {
        email,
        app_name: appName,
      },
      body: {
        reason,
      },
    }

    return this.api.makeRequest<never>(
      `v1/subscriptions/${subscriptionId}`,
      options,
    )
  }

  cancelExtra({
    productId,
    token,
    appName,
  }: {
    productId: string
    token: string
    appName: AppName
  }): Promise<TResponseResult<{ status: boolean }>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        token,
        product_id: productId,
        app_name: appName,
      },
    }

    return this.api.makeRequest<{ status: boolean }>(
      'v1/extras/cancel',
      options,
    )
  }

  cancelExtraByEmail({
    productId,
    email,
    appName,
  }: {
    productId: string
    email: string
    appName: AppName
  }): Promise<TResponseResult<{ status: boolean }>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        email,
        product_id: productId,
        app_name: appName,
      },
    }

    return this.api.makeRequest<{ status: boolean }>(
      'v1/extras/cancel',
      options,
    )
  }

  withdrawSubscriptionByToken({
    subscriptionId,
    token,
    appName,
    customerInfo,
    issuedBy,
  }: {
    subscriptionId: string
    token: string
    appName: string
    customerInfo: { name: string; surname: string; email: string }
    issuedBy: string
  }): Promise<TResponseResult<{ status: boolean }>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      params: {
        token,
        app_name: appName,
      },
      body: {
        customer_info: customerInfo,
        issued_by: issuedBy,
        triggered_by: 'user',
      },
    }

    return this.api.makeRequest<{ status: boolean }>(
      `v1/subscriptions/${subscriptionId}/withdraw`,
      options,
    )
  }

  withdrawSubscriptionByEmail({
    subscriptionId,
    email,
    appName,
    customerInfo,
    issuedBy,
  }: {
    subscriptionId: string
    email: string
    appName: string
    customerInfo: { name: string; surname: string; email: string }
    issuedBy: string
  }): Promise<TResponseResult<{ status: boolean }>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      params: {
        email,
        app_name: appName,
      },
      body: {
        customer_info: customerInfo,
        issued_by: issuedBy,
        triggered_by: 'user',
      },
    }

    return this.api.makeRequest<{ status: boolean }>(
      `v1/subscriptions/${subscriptionId}/withdraw`,
      options,
    )
  }

  extendSubscription(
    extensionToken: string,
  ): Promise<TResponseResult<{ status: boolean }>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      params: {
        extension_token: extensionToken,
      },
    }

    return this.api.makeRequest<{ status: boolean }>(
      `v1/subscriptions/extend`,
      options,
    )
  }
}
